/**
 * DimensionsTab Component
 * Displays detailed breakdown by dimension
 */
import React from 'react';
import { 
  ResultData, 
  AnswerDetail,
  DIMENSION_LABELS, 
  DIMENSION_TO_SECTION,
  getDimensionQuestionRange 
} from '../../types/results';
import { formatTime, getPercentageColorClass } from '../../utils/formatters';

interface DimensionsTabProps {
  results: ResultData;
}

export const DimensionsTab: React.FC<DimensionsTabProps> = ({ results }) => {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-bold text-gray-800">Rapport par dimension</h2>
        <p className="text-sm text-gray-500">Temps et performances pour chaque capacité</p>
      </div>

      {/* Instructions Summary */}
      {results.instruction_times.length > 0 && (
        <InstructionsSummary instructions={results.instruction_times} />
      )}
      
      {/* Dimension Cards */}
      <div className="space-y-4">
        {Object.entries(results.dimension_scores).map(([dimName, dimData]) => (
          <DimensionCard
            key={dimName}
            dimName={dimName}
            dimData={dimData}
            results={results}
          />
        ))}
      </div>
    </div>
  );
};

// Instructions summary component
interface InstructionsSummaryProps {
  instructions: ResultData['instruction_times'];
}

const InstructionsSummary: React.FC<InstructionsSummaryProps> = ({ instructions }) => (
  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
    <h3 className="font-semibold text-blue-800 mb-2 text-sm">
      Temps de lecture des consignes
    </h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
      {instructions.map((instr, idx) => (
        <div key={idx} className="flex justify-between bg-white p-2 rounded">
          <span className="text-gray-700">{instr.section_name || instr.section_id}</span>
          <span className="font-medium text-blue-600">{formatTime(instr.time_spent)}</span>
        </div>
      ))}
    </div>
  </div>
);

// Individual dimension card
interface DimensionCardProps {
  dimName: string;
  dimData: ResultData['dimension_scores'][string];
  results: ResultData;
}

const DimensionCard: React.FC<DimensionCardProps> = ({ dimName, dimData, results }) => {
  // Get section mapping
  const sectionId = DIMENSION_TO_SECTION[dimName] || dimName;
  
  // Filter answers for this dimension
  const [minQ, maxQ] = getDimensionQuestionRange(dimName);
  const dimAnswers = results.answers.filter(a => {
    const match = a.question_id.match(/Q(\d+)/i);
    if (!match) return false;
    const qNum = parseInt(match[1]);
    return qNum >= minQ && qNum <= maxQ;
  });
  
  // Calculate stats
  const totalAnswerTime = dimAnswers.reduce((sum, a) => sum + (a.time_taken || 0), 0);
  const totalPreRuleTime = dimAnswers.reduce((sum, a) => sum + (a.pre_rule_time || 0), 0);
  const totalDistractionTime = dimAnswers.reduce((sum, a) => sum + (a.distraction_time || 0), 0);
  const totalDistractionMoves = dimAnswers.reduce((sum, a) => sum + (a.distraction_moves || 0), 0);
  const correctCount = dimAnswers.filter(a => a.is_correct).length;
  const avgTimePerQuestion = dimAnswers.length > 0 ? Math.round(totalAnswerTime / dimAnswers.length) : 0;
  
  // Check what data exists
  const hasInstruction = dimData.instruction_time > 0;
  const hasPreRule = totalPreRuleTime > 0;
  const hasDistraction = totalDistractionTime > 0;
  
  const headerColorClass = getPercentageColorClass(dimData.percentage);

  return (
    <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
      {/* Header */}
      <div className={`p-4 ${headerColorClass} text-white`}>
        <div className="flex justify-between items-center">
          <h3 className="font-bold text-lg">{DIMENSION_LABELS[dimName] || dimName}</h3>
          <div className="text-right">
            <span className="text-2xl font-bold">{dimData.score}/{dimData.max_score}</span>
            <span className="ml-2 text-sm opacity-90">({dimData.percentage.toFixed(0)}%)</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Stats Row */}
        <div className="flex flex-wrap gap-4 text-sm">
          <span><strong>{dimAnswers.length}</strong> questions</span>
          <span className="text-green-600"><strong>{correctCount}</strong> correctes</span>
          <span className="text-red-600"><strong>{dimAnswers.length - correctCount}</strong> incorrectes</span>
          <span className="text-primary"><strong>{avgTimePerQuestion}s</strong> moyenne</span>
        </div>

        {/* Time Details */}
        <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
          {hasInstruction && (
            <TimeRow label="Consigne" value={formatTime(dimData.instruction_time)} valueColor="text-blue-600" />
          )}
          {hasPreRule && (
            <TimeRow label="Règles" value={formatTime(totalPreRuleTime)} />
          )}
          {hasDistraction && (
            <TimeRow 
              label={`Puzzle (${totalDistractionMoves} mouvements)`} 
              value={formatTime(totalDistractionTime)} 
            />
          )}
          <TimeRow label="Réponses" value={formatTime(totalAnswerTime)} />
          <div className="flex justify-between pt-1 border-t border-gray-200 font-bold">
            <span>Total</span>
            <span className="text-primary">
              {formatTime(
                (hasInstruction ? dimData.instruction_time : 0) + 
                totalPreRuleTime + 
                totalDistractionTime + 
                totalAnswerTime
              )}
            </span>
          </div>
        </div>

        {/* Questions List */}
        <QuestionsList answers={dimAnswers} />
      </div>
    </div>
  );
};

// Time row component
interface TimeRowProps {
  label: string;
  value: string;
  valueColor?: string;
}

const TimeRow: React.FC<TimeRowProps> = ({ label, value, valueColor = '' }) => (
  <div className="flex justify-between">
    <span className="text-gray-600">{label}</span>
    <span className={`font-medium ${valueColor}`}>{value}</span>
  </div>
);

// Collapsible questions list
interface QuestionsListProps {
  answers: AnswerDetail[];
}

const QuestionsList: React.FC<QuestionsListProps> = ({ answers }) => (
  <details className="text-sm">
    <summary className="cursor-pointer font-medium text-gray-700 hover:text-primary">
      Voir les {answers.length} questions
    </summary>
    <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
      {answers.map((answer, idx) => (
        <div 
          key={idx} 
          className={`flex justify-between items-center px-2 py-1 rounded ${
            answer.is_correct ? 'bg-green-50' : 'bg-red-50'
          }`}
        >
          <span className="flex items-center gap-2">
            <span className={answer.is_correct ? 'text-green-600' : 'text-red-600'}>
              {answer.is_correct ? '✓' : '✗'}
            </span>
            <span>{answer.question_id}</span>
          </span>
          <span className="text-gray-500">{answer.time_taken}s / {answer.points_earned}pts</span>
        </div>
      ))}
    </div>
  </details>
);

export default DimensionsTab;
